import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** Accordion instances inside the mobile `header-drawer` (expand-first, nested nav). */
  get #isHeaderDrawerMenuAccordion() {
    return this.closest('header-drawer') != null;
  }

  /** The root `<details>` that opens the whole drawer (ancestor of nested menu accordions). */
  #mainMenuDrawerDetails() {
    const el = this.closest('#Details-menu-drawer-container');
    return el instanceof HTMLDetailsElement ? el : null;
  }

  /**
   * Expand-first uses `open-by-default-on-mobile`. On a wide viewport that still means "mobile nav"
   * while the drawer is open — so treat (viewport < 750px) OR (main drawer open) as mobile for
   * expand-first only.
   */
  #isEffectiveMobileForExpandFirst() {
    if (!this.#isHeaderDrawerMenuAccordion) return isMobileBreakpoint();
    return isMobileBreakpoint() || this.#mainMenuDrawerDetails()?.hasAttribute('open') === true;
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    const mainDrawer = this.#mainMenuDrawerDetails();
    if (this.#isHeaderDrawerMenuAccordion && mainDrawer) {
      mainDrawer.addEventListener(
        'toggle',
        (e) => {
          if (e.target !== mainDrawer) return;
          this.#setDefaultOpenState();
        },
        { signal }
      );
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Nested <details> inside the mobile menu drawer (and Chrome device-mode emulation) often
    // does not run the native summary activation reliably. Take over toggling explicitly.
    event.preventDefault();
    this.details.open = !this.details.open;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   *
   * When those attributes are absent, do not touch `details.open`. Otherwise we overwrite the
   * native `<details open>` from Liquid and force closed on every resize — breaking expand/collapse
   * inside the mobile menu (regression vs native-only accordion).
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    const openMobile = this.hasAttribute('open-by-default-on-mobile');
    const openDesktop = this.hasAttribute('open-by-default-on-desktop');

    if (!openMobile && !openDesktop) {
      return;
    }

    const effectiveMobile = this.#isEffectiveMobileForExpandFirst();
    const next =
      (effectiveMobile && openMobile) ||
      (!effectiveMobile && openDesktop);
    this.details.open = next;
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
